import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { eQMSScopeConstants } from '../eqms-scope-constants';
import { EqmsScopeService } from '../eqms-scope.service';
import { UserListLookup } from '../../shared/models/users/user-list-lookup';
import { EqmsScopeManagementService } from '../../shared/services/eqms-scope-management.service';
import { UsersService } from '../../shared/services/users.service';
import { BulkUserScopeSelection } from '../models/bulk-user-scope-selection';
import { BulkScopeUpdateResponse } from '../models/bulk-scope-updater-response';

@Component({
  selector: 'app-bulk-user-scope-slider',
  templateUrl: './bulk-user-scope-slider.component.html',
  styleUrls: ['./bulk-user-scope-slider.component.scss']
})
export class BulkUserScopeSliderComponent implements OnInit, OnDestroy {
  @Output() isBulkUserScopeSliderActive = new EventEmitter<boolean>();

  @Input() isBulkUserUpdateSliderOpen: boolean = false;
  @Input() selectedScope: { eQMSScopeId?: number } | null = null;

  topBarTitle: string = 'Bulk Add/Remove Users';
  valueField: string = 'puid';
  labelField: string = 'name';
  userList: UserListLookup[] = [];
  filteredPrimaryUsers: UserListLookup[] = [];
  filteredOtherUsers: UserListLookup[] = [];
  filteredRemoveUsers: UserListLookup[] = [];
  hasSelection: boolean = false;
  isLoadingUsers: boolean = false;
  isSubmitting: boolean = false;
  bulkUserScopeForm: FormGroup;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly usersService: UsersService,
    private readonly eqmsScopeService: EqmsScopeService,
    private readonly eqmsScopeManagementService: EqmsScopeManagementService,
    private readonly messageService: MessageService
  ) {
    this.bulkUserScopeForm = this.formBuilder.group({
      addAsPrimaryUsers: [[]],
      addAsOtherUsers: [[]],
      removeFromOtherUsers: [[]]
    });
  }

  ngOnInit(): void {
    this.initializeFormState();
    this.loadUsers();
    this.eqmsScopeService.closeAndResetBulkUserScopeSlider$.subscribe((isClose) => {
      if (isClose) { 
        this.closeSlider()
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeSliderWithConfirmation(): void {
    this.eqmsScopeService.openConfirmationDialogForBulkUserSlider();
  }

  closeSlider(): void {
    this.isBulkUserUpdateSliderOpen = false;
    this.resetSlider();
    this.isBulkUserScopeSliderActive.emit(false);
  }

  submitBulkUserScopeForm(): void {
    if (!this.hasSelection || this.isLoadingUsers || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.eqmsScopeService
      .updateBulkUserScopeUsers(this.mapFormToBulkUserScopeRequest())
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: eQMSScopeConstants.Severity_Success,
            summary: eQMSScopeConstants.Scope_Bulk_User_Update_Success_Message
          });          
         this.closeSlider();
         },
        error: () => {
          this.messageService.add({
            severity: eQMSScopeConstants.Severity_Err,
            summary: eQMSScopeConstants.Scope_Bulk_User_Update_Error_Message
          });
        }
      });
  }

  private initializeFormState(): void {
    const initial = this.bulkUserScopeForm.getRawValue();
    this.updateHasSelection(initial);
    this.updateFilteredLists(initial);
    this.bulkUserScopeForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((formValue) => {
      this.updateHasSelection(formValue);
      this.updateFilteredLists(formValue);
    });
  }

  private loadUsers(): void {
    this.isLoadingUsers = true;
    this.usersService.getUsersForBulkUserScopeUpdate().pipe(takeUntil(this.destroy$)).subscribe({
      next: (users: UserListLookup[]) => {
        this.userList = [...(users ?? [])].sort((a, b) => a.name.localeCompare(b.name));
        this.updateFilteredLists(this.bulkUserScopeForm.getRawValue());
        this.isLoadingUsers = false;
      },
      error: () => {
        this.userList = [];
        this.isLoadingUsers = false;
      }
    });
  }

  private updateHasSelection(formValue: any): void {
    this.hasSelection = [
      formValue.addAsPrimaryUsers,
      formValue.addAsOtherUsers,
      formValue.removeFromOtherUsers
    ].some((selectedUsers) => Array.isArray(selectedUsers) && selectedUsers.length > 0);
  }

  private updateFilteredLists(formValue: any): void {
    const primarySelected = new Set<string>(formValue.addAsPrimaryUsers ?? []);
    const otherSelected = new Set<string>(formValue.addAsOtherUsers ?? []);
    const removeSelected = new Set<string>(formValue.removeFromOtherUsers ?? []);

    const primary: UserListLookup[] = [];
    const other: UserListLookup[] = [];
    const remove: UserListLookup[] = [];

    // Single pass — O(n) with O(1) Set lookups instead of three O(n·m) filter passes
    for (const user of this.userList) {
      const { puid } = user;
      if (!otherSelected.has(puid)) {
        // Primary and Remove share the same exclusion rule (addAsOtherUsers)
        primary.push(user);
        remove.push(user);
      }
      if (!primarySelected.has(puid) && !removeSelected.has(puid)) {
        other.push(user);
      }
    }

    this.filteredPrimaryUsers = primary;
    this.filteredOtherUsers = other;
    this.filteredRemoveUsers = remove;
  }

  private mapFormToBulkUserScopeRequest(): BulkUserScopeSelection {
    const formValue = this.bulkUserScopeForm.getRawValue();

    return {
      scopeId: this.selectedScope?.eQMSScopeId ?? null,
      addAsPrimaryUsers: formValue.addAsPrimaryUsers ?? [],
      addAsOtherUsers: formValue.addAsOtherUsers ?? [],
      removeFromOtherUsers: formValue.removeFromOtherUsers ?? []
    };
  }

  private resetSlider(): void {
    this.bulkUserScopeForm.reset({
      addAsPrimaryUsers: [],
      addAsOtherUsers: [],
      removeFromOtherUsers: []
    });
    this.hasSelection = false;
    this.isSubmitting = false;
    this.updateFilteredLists({ addAsPrimaryUsers: [], addAsOtherUsers: [], removeFromOtherUsers: [] });
  }
}
